import { v4 as uuidv4 } from 'uuid';
import { createSessionBoundSupabaseClient, supabase } from '@/services/supabase';
import { globalState } from '@/state/globalState';
import { IS_CHECKLIST_RECOVERY_DIAGNOSTIC } from '@/config/recoveryDiagnostic';

export type PartnerPermission = 'hidden' | 'read' | 'edit';
export interface PartnerOwnerField {
  id: string;
  nome: string;
  grupo: string;
  tipo: string;
  obrigatorio: boolean;
  original_campo_id: string | null;
  snapshot_field_id?: string | null;
}
export interface PartnerOwnerState {
  revision: number;
  enabled: boolean;
  touched: boolean;
  active: boolean;
  active_link: { id: string; expires_at: string; status: string } | null;
  receipt_links?: Array<{ id: string; expires_at: string; completed_at: string }>;
  checklist: { id: string; status: string; data_conclusao: string | null };
  empresa: { id: string; nome: string };
  fields: PartnerOwnerField[];
  value_rows: Record<string, unknown>[];
}
export interface PartnerOwnerScope { companyId: string; userId: string; checklistId: string }

const messages: Record<string, string> = {
  partner_session_active: 'O parceiro está preenchendo. Encerre o acesso para retomar a edição.',
  partner_revision_conflict: 'O checklist recebeu alterações. Atualize os dados antes de tentar novamente.',
  partner_revision_required: 'Este checklist foi atualizado pelo parceiro. Sincronize e abra novamente.',
  partner_feature_disabled: 'O acesso de parceiros não está habilitado para esta empresa.',
  partner_checklist_not_editable: 'Este checklist já foi concluído.',
  partner_forbidden: 'Você não tem acesso a este checklist.',
  partner_admin_required: 'Somente o administrador pode liberar ou encerrar o link.',
  partner_snapshot_invalid: 'Não foi possível confirmar os campos deste checklist. Contate o suporte.',
};
export const partnerOwnerErrorMessage = (value: unknown): string => {
  const raw = value instanceof Error ? value.message : String(value);
  return messages[raw] || (raw.startsWith('Existem ') ? raw : 'Não foi possível confirmar a operação. Confira a conexão e tente novamente.');
};

export const partnerOwnerIdentityMatches = (scope: PartnerOwnerScope): boolean =>
  globalState.user.isAuthenticated.get() === true &&
  globalState.user.profile.get()?.id === scope.userId &&
  globalState.user.profile.get()?.empresa_id === scope.companyId &&
  globalState.user.session.get()?.user?.id === scope.userId;

/** Cada chamada fica presa à sessão validada; nunca depende de uma troca global de usuário. */
export async function callPartnerOwner<T>(scope: PartnerOwnerScope, action: string, payload: Record<string, unknown> = {}): Promise<T> {
  if (IS_CHECKLIST_RECOVERY_DIAGNOSTIC && action !== 'owner_state') throw new Error('Escrita bloqueada no modo de recuperação.');
  if (!partnerOwnerIdentityMatches(scope)) throw new Error('A sessão do checklist mudou.');
  const { data: auth, error: authError } = await supabase.auth.getSession();
  if (authError || !auth.session?.access_token || auth.session.user.id !== scope.userId ||
      !partnerOwnerIdentityMatches(scope)) throw new Error('A sessão do checklist mudou.');
  const client = createSessionBoundSupabaseClient(auth.session.access_token);
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 20000);
  try {
    const { data, error } = await client.functions.invoke('checklist-partner', {
      body: { action, checklistId: scope.checklistId, ...payload },
      signal: controller.signal,
    });
    if (!partnerOwnerIdentityMatches(scope)) throw new Error('A sessão do checklist mudou.');
    if (error) {
      let code: string | undefined;
      try { code = (await error.context?.json())?.error; } catch { /* Resposta sem JSON mantém mensagem segura. */ }
      throw new Error(code || 'partner_request_failed');
    }
    return data as T;
  } finally { clearTimeout(timeout); }
}

export const getPartnerOwnerState = (scope: PartnerOwnerScope) =>
  callPartnerOwner<PartnerOwnerState>(scope, 'owner_state');

export const newPartnerRequestId = () => uuidv4();
